import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    UploadCloud,
    ShieldCheck,
    ArrowUpRight,
    Phone,
} from "lucide-react";
import { COMPANY } from "@/lib/compliance";

/**
 * Pre-upload disclosure modal. Gates the Dropbox File Request link with
 * a Phase-2 compliance disclosure (third-party provider notice, sensitive
 * info warning, and the categories of recipients we may share documents
 * with). Visitor must click "Continue to Secure Upload" to proceed in a
 * new tab, or "Contact Us Before Uploading Sensitive Documents" to fall
 * back to the Contact page.
 */
export default function SecureUploadDialog({ trigger, dropboxUrl }) {
    const [open, setOpen] = useState(false);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{trigger}</DialogTrigger>
            <DialogContent
                data-testid="secure-upload-dialog"
                className="max-w-xl border border-[rgba(212,175,55,0.35)] bg-[#0b0e13] text-brand-ivory shadow-[0_30px_60px_-20px_rgba(0,0,0,0.85)]"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-3 font-serif text-2xl text-brand-ivory">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[rgba(212,175,55,0.45)] bg-[#0d1117]">
                            <UploadCloud
                                aria-hidden="true"
                                className="h-5 w-5 text-brand-gold2"
                            />
                        </span>
                        Before You Upload
                    </DialogTitle>
                    <DialogDescription className="mt-3 text-sm leading-relaxed text-brand-muted">
                        Please review this disclosure before continuing to
                        the secure upload page.
                    </DialogDescription>
                </DialogHeader>

                <div
                    data-testid="secure-upload-dialog-disclosure"
                    className="mt-4 space-y-3 text-xs leading-relaxed text-brand-muted"
                >
                    <p>
                        Document upload is provided through Dropbox or another
                        third-party secure document provider. Files are
                        delivered to {COMPANY.shortName}&apos;s intake folder
                        and are not intended to be publicly accessible or
                        viewable by other users.
                    </p>
                    <p>
                        <span className="text-brand-goldSoft">
                            Do not upload:{" "}
                        </span>
                        Social Security numbers, payment information, bank
                        information, medical records, claim documents,
                        identity documents, passwords, or other highly
                        sensitive information unless we specifically request
                        it through an approved secure channel.
                    </p>
                    <p>
                        Uploaded documents may be reviewed, retained, shared
                        with insurance carriers, MGAs, wholesalers, TPAs,
                        licensed producers, or service providers as needed to
                        respond to your request, evaluate coverage options,
                        prepare submissions, comply with law, or maintain
                        records. See our{" "}
                        <Link
                            to="/privacy"
                            data-testid="secure-upload-privacy-link"
                            className="text-brand-gold2 underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-sm"
                        >
                            Privacy Notice
                        </Link>{" "}
                        and{" "}
                        <Link
                            to="/terms"
                            data-testid="secure-upload-terms-link"
                            className="text-brand-gold2 underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-sm"
                        >
                            Terms of Service
                        </Link>
                        .
                    </p>
                </div>

                <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-stretch">
                    <a
                        data-testid="secure-upload-continue"
                        href={dropboxUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Continue to secure upload — opens Dropbox in a new tab"
                        onClick={() => setOpen(false)}
                        className="group inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-brand-gold bg-brand-gold px-5 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-brand-bg shadow-[0_18px_36px_-18px_rgba(212,175,55,0.6)] transition-all hover:bg-brand-gold2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg active:scale-[0.98]"
                    >
                        <ShieldCheck aria-hidden="true" className="h-4 w-4" />
                        Continue to Secure Upload
                        <ArrowUpRight
                            aria-hidden="true"
                            className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        />
                    </a>
                    <Link
                        data-testid="secure-upload-contact-first"
                        to="/contact"
                        onClick={() => setOpen(false)}
                        className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-brand-gold/60 bg-[#0b0e13] px-5 py-3 text-xs uppercase tracking-[0.2em] text-brand-gold2 transition-colors hover:bg-brand-gold hover:text-brand-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                    >
                        <Phone aria-hidden="true" className="h-4 w-4" />
                        Contact Us First
                    </Link>
                </div>
                <p className="mt-3 text-center text-[11px] text-brand-muted">
                    Need a secure channel? Call{" "}
                    <a
                        href={`tel:${COMPANY.phoneTel}`}
                        className="text-brand-gold2 underline-offset-4 hover:underline"
                    >
                        {COMPANY.phone}
                    </a>{" "}
                    before sending anything sensitive.
                </p>
            </DialogContent>
        </Dialog>
    );
}
